import { useEffect, useState } from "react";
import { Link, useParams, useNavigate } from "react-router-dom";
import api from "@/lib/api";
import { toast } from "sonner";
import { Edit, Plus, Building2, Globe, Instagram, Youtube, Facebook, Linkedin, Archive, ArchiveRestore } from "lucide-react";
import ResearchPanel from "@/components/ResearchPanel";
import SocialProfilesPanel from "@/components/SocialProfilesPanel";

export default function ClientDetail() {
    const { id } = useParams();
    const navigate = useNavigate();
    const [c, setC] = useState(null);
    const [projects, setProjects] = useState([]);

    useEffect(() => {
        api.get(`/clients/${id}`).then(({data}) => setC(data));
        api.get(`/projects?client_id=${id}&include_archived=true`).then(({data}) => setProjects(data));
    }, [id]);

    if (!c) return <div className="p-10 text-zinc-500">Loading…</div>;

    return (
        <div className="p-6 sm:p-8 lg:p-10 max-w-7xl mx-auto" data-testid="client-detail-page">
            <div className="flex items-start justify-between gap-6 mb-8">
                <div className="flex gap-4">
                    <div className="w-16 h-16 bg-zinc-900 border border-zinc-800 grid place-items-center rounded-sm shrink-0">
                        <Building2 className="w-7 h-7 text-zinc-400" />
                    </div>
                    <div>
                        <div className="label-micro mb-1">{c.industry || "Client"}</div>
                        <h1 className="font-display text-4xl sm:text-5xl font-medium tracking-tight">{c.company_name}</h1>
                        <div className="flex flex-wrap gap-3 mt-3 text-zinc-500">
                            {c.website && <a href={c.website} target="_blank" rel="noreferrer" className="hover:text-white"><Globe className="w-4 h-4" /></a>}
                            {c.instagram && <a href={c.instagram} target="_blank" rel="noreferrer" className="hover:text-white"><Instagram className="w-4 h-4" /></a>}
                            {c.facebook && <a href={c.facebook} target="_blank" rel="noreferrer" className="hover:text-white"><Facebook className="w-4 h-4" /></a>}
                            {c.youtube && <a href={c.youtube} target="_blank" rel="noreferrer" className="hover:text-white"><Youtube className="w-4 h-4" /></a>}
                            {c.linkedin && <a href={c.linkedin} target="_blank" rel="noreferrer" className="hover:text-white"><Linkedin className="w-4 h-4" /></a>}
                        </div>
                    </div>
                </div>
                <div className="flex gap-2">
                    <Link to={`/clients/${c.id}/edit`} data-testid="client-edit" className="btn-secondary"><Edit className="w-4 h-4" /> Edit</Link>
                    <Link to={`/projects/new?client_id=${c.id}`} data-testid="client-new-project" className="btn-primary"><Plus className="w-4 h-4" /> New project</Link>
                    <ClientArchiveAction c={c} reload={() => api.get(`/clients/${id}`).then(({data}) => setC(data))} navigate={navigate} />
                </div>
            </div>

            {c.is_archived && (
                <div
                    data-testid="client-archived-banner"
                    className="mb-6 border border-amber-500/30 bg-amber-500/5 rounded-sm px-4 py-3 flex items-center gap-3 text-sm text-amber-200"
                >
                    <Archive className="w-4 h-4" />
                    <span>
                        This client is archived
                        {c.archived_at ? ` on ${new Date(c.archived_at).toLocaleString()}` : ""}.
                        Hidden from the active client list. All data preserved.
                    </span>
                </div>
            )}

            <div className="grid grid-cols-1 lg:grid-cols-3 gap-4 mb-8">
                <div className="lg:col-span-2 card-base p-6 space-y-4">
                    <Field label="Brand Story" v={c.brand_story} />
                    <Field label="Vision" v={c.vision} />
                    <Field label="Mission" v={c.mission} />
                    <Field label="Target Audience" v={c.target_audience} />
                    <Field label="Competitors" v={c.competitors} />
                    <Field label="Tone of Voice" v={c.tone_of_voice} />
                </div>
                <div className="card-base p-6 space-y-4">
                    <Field label="Brand Colors" v={c.brand_colors} mono />
                    <Field label="Fonts" v={c.fonts} mono />
                    <Field label="Mandatory Elements" v={c.mandatory_elements} />
                    <Field label="Things to Avoid" v={c.things_to_avoid} />
                    <Field label="Current Offers" v={c.current_offers} />
                    <Field label="Campaign Goals" v={c.campaign_goals} />
                </div>
            </div>

            {/* Phase 2A-2B — website research panel */}
            <div className="mb-8">
                <ResearchPanel clientId={c.id} defaultUrl={c.website} />
            </div>

            {/* Phase 2E-lite — Safe social profile registry */}
            <div className="mb-8">
                <SocialProfilesPanel clientId={c.id} />
            </div>

            <div className="flex items-center justify-between mb-4">
                <h2 className="font-display text-2xl">Projects</h2>
                <Link to={`/projects/new?client_id=${c.id}`} className="btn-secondary text-xs"><Plus className="w-3.5 h-3.5" /> Add project</Link>
            </div>
            <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-4">
                {projects.map(p => (
                    <Link key={p.id} to={`/projects/${p.id}`} className={`card-base card-hover p-5 ${p.is_archived ? "opacity-70" : ""}`} data-testid={`client-project-${p.id}`}>
                        <div className="flex items-center justify-between gap-2">
                            <div className="font-medium truncate">{p.title}</div>
                            {p.is_archived && (
                                <span className="text-[10px] font-mono uppercase tracking-wider px-2 py-0.5 rounded-sm border border-amber-500/40 text-amber-300 shrink-0">
                                    Archived
                                </span>
                            )}
                        </div>
                        <div className="text-[10px] uppercase tracking-wider text-zinc-500 font-mono mt-1">{p.project_type.replaceAll("_", " ")}</div>
                        <div className="text-xs text-zinc-500 mt-3">Stage: <span className="text-zinc-300">{p.current_stage}</span></div>
                    </Link>
                ))}
                {projects.length === 0 && (
                    <div className="col-span-full text-sm text-zinc-500 text-center py-12 border border-dashed border-zinc-900 rounded-sm">
                        No projects yet for this client.
                    </div>
                )}
            </div>
        </div>
    );
}

const Field = ({ label, v, mono }) => (
    <div>
        <div className="label-micro mb-1.5">{label}</div>
        <div className={`text-sm text-zinc-200 whitespace-pre-wrap ${mono ? "font-mono" : ""}`}>{v || <span className="text-zinc-600">—</span>}</div>
    </div>
);

/** Phase N.6 — Client archive header action.
 *  Fetches /clients/{id}/archive-impact to show the user how many active
 *  projects will cascade-archive, then calls /clients/{id}/archive
 *  (with archive_projects=true) or /clients/{id}/unarchive accordingly. */
function ClientArchiveAction({ c, reload, navigate }) {
    const [busy, setBusy] = useState(false);

    const onUnarchive = async () => {
        if (busy) return;
        setBusy(true);
        try {
            await api.post(`/clients/${c.id}/unarchive`);
            toast.success("Client restored to active list.");
            await reload();
        } catch (err) {
            toast.error(err.userMessage || err.message || "Could not unarchive");
        } finally { setBusy(false); }
    };

    const onArchive = async () => {
        if (busy) return;
        setBusy(true);
        let impact;
        try {
            const r = await api.get(`/clients/${c.id}/archive-impact`);
            impact = r.data;
        } catch (err) {
            toast.error(err.userMessage || err.message || "Could not load impact");
            setBusy(false); return;
        }
        const active = impact.active_projects || 0;
        let archiveProjects = false;
        if (active > 0) {
            const proceed = window.confirm(
                `This client has ${active} active project${active === 1 ? "" : "s"}.\n\n` +
                `Click OK to archive the client AND all ${active} project${active === 1 ? "" : "s"} (recommended).\n` +
                `Click Cancel to abort.\n\n` +
                `Outputs, versions and approvals are always preserved.`
            );
            if (!proceed) { setBusy(false); return; }
            archiveProjects = true;
        } else {
            const proceed = window.confirm(
                `Archive "${c.company_name}"? It will be hidden from the active client list.`
            );
            if (!proceed) { setBusy(false); return; }
        }
        try {
            await api.post(`/clients/${c.id}/archive${archiveProjects ? "?archive_projects=true" : ""}`);
            toast.success(
                archiveProjects
                    ? `Client and ${active} project${active === 1 ? "" : "s"} archived.`
                    : "Client archived."
            );
            navigate("/clients");
        } catch (err) {
            toast.error(err.userMessage || err.message || "Could not archive");
            setBusy(false);
        }
    };

    if (c.is_archived) {
        return (
            <button
                type="button"
                data-testid="client-unarchive-btn"
                onClick={onUnarchive}
                disabled={busy}
                className="btn-secondary disabled:opacity-50 flex items-center gap-1.5"
            >
                <ArchiveRestore className="w-4 h-4" />
                {busy ? "Restoring…" : "Unarchive"}
            </button>
        );
    }

    return (
        <button
            type="button"
            data-testid="client-archive-btn"
            onClick={onArchive}
            disabled={busy}
            className="btn-secondary disabled:opacity-50 flex items-center gap-1.5"
            title="Soft-archive client (preserves all data)"
        >
            <Archive className="w-4 h-4" />
            {busy ? "Archiving…" : "Archive"}
        </button>
    );
}
